import type { Board } from "../../board";
import { getMobGoldValue } from "../../economy/mobGoldValues";
import { DamageType } from "../../damageType";
import { ALL_DIRECTIONS, directionDelta } from "../../direction";
import { Explosion } from "../../effects/explosion";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../../effects/movementEffect";
import { PRIORITY_DEFAULT, type ProcessableThing, type ProjectileHittable } from "../../interfaces";
import { PhysicalThingWithHp } from "../../physicalThingWithHp";
import { randomChance, randomElement } from "../../random";
import { deserializeXY } from "../../serialization";
import type { Thing } from "../../thing";
import type { XY } from "../../xy";
import { ZOrder } from "../../zOrder";
import { GoldDrop } from "../goldDrop";
import { MobSpawnerConfig, type MobSpawnConfigEntry } from "./mobSpawnerConfig";
import { Portal2Part } from "./portal2Part";

export class Portal2 extends PhysicalThingWithHp implements ProcessableThing, ProjectileHittable {
	static key = "Portal2";
	private parts: Portal2Part[] = [];
	private mobSpawnerConfig: MobSpawnerConfig = new MobSpawnerConfig();
	spawnProbability: number = 0.12;
	gold: number = 0;

	constructor(xy: XY) {
		super(xy, true);
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
	}

	isPassable(): boolean {
		return false;
	}

	override isVisible(): boolean {
		return true;
	}

	override getZOrder(): number {
		return ZOrder.PORTAL;
	}

	override isHideUnderneath(): boolean {
		return true;
	}

	onProjectileHit(_projectile: unknown): void {}

	isHittable(): boolean {
		return true;
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	getBaseGoldValue(): number {
		return getMobGoldValue(Portal2.key);
	}

	processTurn(_board: Board): void {
		if (!randomChance(this.spawnProbability)) return;

		// Get spawn coordinates: adjacent cells that have a Portal2Part and no mob
		const okCoords: XY[] = [];
		for (const dir of ALL_DIRECTIONS) {
			const proposedXY = this.xy.add(directionDelta(dir));
			if (!this.board!.isInBounds(proposedXY)) continue;
			const hasPart = this.board!.getThingsAt(proposedXY).some((t) => t instanceof Portal2Part);
			if (hasPart && !this.board!.hasMob(proposedXY)) {
				okCoords.push(proposedXY);
			}
		}
		if (okCoords.length === 0) return;

		const mobDestination = randomElement(okCoords);
		if (!mobDestination) return;

		const mob = this.mobSpawnerConfig.getMobToSpawn();
		if (!mob) return;

		mob.xy = mobDestination;
		this.board!.addThing(mob);
		mob.gold = this.board!.spendGoldBudget(mob.getBaseGoldValue());
		this.board!.addEffect(new MovementEffect(mob, this.xy, mobDestination, STANDARD_MOVEMENT_TIME * 1.5));
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		this.board!.stats.recordSpawnerDestroyed();
		this.board!.addEffect(new Explosion(this.xy));
		for (const part of this.parts) {
			if (part.active) {
				this.board!.addEffect(new Explosion(part.xy));
				part.kill(null, this);
			}
		}
		if (this.gold > 0 && this.board) {
			GoldDrop.spawnOrMerge(this.board, this.xy, this.gold);
		}
	}

	addPart(part: Portal2Part): void {
		this.parts.push(part);
		part.setMaster(this);
	}

	getParts(): Portal2Part[] {
		return this.parts;
	}

	setSpawnProbability(prob: number): void {
		this.spawnProbability = prob;
	}

	getMobSpawnerConfig(): MobSpawnerConfig {
		return this.mobSpawnerConfig;
	}

	setMobSpawnerConfig(config: MobSpawnerConfig): void {
		this.mobSpawnerConfig = config;
	}

	addConfig(entry: MobSpawnConfigEntry): void {
		this.mobSpawnerConfig.addEntry(entry);
	}

	static create(board: Board, targetXY: XY): Portal2 | null {
		const neighbors = targetXY.getNeighbors();
		for (const xy of neighbors) {
			if (!board.isInBounds(xy) || !board.isPassable(xy)) return null;
			if (board.getThingsAt(xy).length > 0) return null;
		}
		const portal = new Portal2(targetXY);
		board.addThing(portal);
		for (const xy of neighbors) {
			const part = new Portal2Part(xy);
			board.addThing(part);
			portal.addPart(part);
		}
		return portal;
	}

	serialize(): Record<string, unknown> {
		const data: Record<string, unknown> = {
			...super.serialize(),
			spawnProbability: this.spawnProbability,
			mobSpawnerConfig: this.mobSpawnerConfig.serialize(),
		};
		if (this.gold > 0) data.gold = this.gold;
		return data;
	}

	static deserialize(data: any): Portal2 {
		const p = new Portal2(deserializeXY(data.xy));
		p.restoreBaseFields(data);
		if (data.spawnProbability !== undefined) p.spawnProbability = data.spawnProbability;
		if (data.mobSpawnerConfig) p.mobSpawnerConfig = MobSpawnerConfig.deserialize(data.mobSpawnerConfig);
		p.gold = data.gold ?? 0;
		return p;
	}
}
